import {getTestProps} from '@benchling/js-test-utils';
import classNames from 'classnames';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {ElementMouseEvent} from 'util/Types';

export interface BaseReorderableItemClassProps {
  isDragging?: boolean;
  onDragStart?: (e: ElementMouseEvent) => void | null;
  overrideItemClass?: React.ComponentType<Omit<BaseReorderableItemClassProps, 'overrideItemClass'>>;
}

export interface ReorderableListItemProps<T extends BaseReorderableItemClassProps> {
  itemClass: React.ComponentType<T>;
  itemProps: T;
  onDragStart?: (e: ElementMouseEvent, index: number) => void;
  index?: number;
  isInvisible?: boolean;
  dragLayoutParams?: {
    translateY: number;
    height: number;
    width: number;
  };
}

class ReorderableListItem<T extends BaseReorderableItemClassProps> extends React.Component<
  ReorderableListItemProps<T>
> {
  static defaultProps = {
    isInvisible: false,
  };

  private handleDragStart = (e: ElementMouseEvent): void => {
    if (e.button !== 0) {
      return;
    }
    if (!assertAndContinue(this.props.index != null, 'Cannot start dragging on a dragged item.')) {
      return;
    }
    if (!assertAndContinue(this.props.onDragStart != null, 'Expected a non-null onDragStart prop')) {
      return;
    }
    this.props.onDragStart!(e, this.props.index!);
  };

  override render(): JSX.Element {
    const isDragging = this.props.dragLayoutParams != null;

    let style = {};
    if (isDragging) {
      // When this component is a "fake" dragged item, it is absolutely positioned rather than having its
      // height, width, and position specified by its parent, so they need to be given as parameters. The
      // position is based on the mouse position, and the height and width were measured at the start of the
      // drag operation.
      style = {
        transform: `translateY(${this.props.dragLayoutParams.translateY}px)`,
        height: this.props.dragLayoutParams.height,
        width: this.props.dragLayoutParams.width,
      };
    }
    const ItemClass = this.props.itemProps.overrideItemClass || this.props.itemClass;
    return (
      <div
        className={classNames('reorderableListItem', {
          'is-dragging': isDragging,
          'is-invisible': this.props.isInvisible,
        })}
        style={style}
        {...getTestProps({component: 'ReorderableListItem', element: 'root'})}
      >
        <ItemClass
          {...this.props.itemProps}
          isDragging={isDragging}
          onDragStart={isDragging ? null : this.handleDragStart}
        />
      </div>
    );
  }
}

export default ReorderableListItem;
